import { type ImportDefinitionDTO, ImportDefinitionStatus } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'

/**
 * Props for {@link ImportDefinitionList}.
 *
 * @property definitions - The tenant's definitions, versions newest first.
 * @property canManage - Shows New, Edit and Activate; a reader sees the list only.
 * @property onNew - Starts a new definition.
 * @property onEdit - Opens a version in the designer.
 * @property onActivate - Activates a DRAFT version.
 */
export interface ImportDefinitionListProps {
	definitions: readonly ImportDefinitionDTO[]
	canManage: boolean
	onNew: () => void
	onEdit: (key: string, version: number) => void
	onActivate: (key: string, version: number) => void
}

const VARIANT: Record<ImportDefinitionStatus, 'success' | 'secondary' | 'muted'> = {
	[ImportDefinitionStatus.ACTIVE]: 'success',
	[ImportDefinitionStatus.DRAFT]: 'secondary',
	[ImportDefinitionStatus.RETIRED]: 'muted',
}

interface VersionRowProps {
	definitionKey: string
	version: ImportDefinitionDTO['versions'][number]
	canManage: boolean
	onEdit: ImportDefinitionListProps['onEdit']
	onActivate: ImportDefinitionListProps['onActivate']
}

function VersionRow({ definitionKey, version: v, canManage, onEdit, onActivate }: VersionRowProps) {
	return (
		<li className="flex items-center gap-2">
			<span>Version {v.version}</span>
			<Badge variant={VARIANT[v.status]}>{v.status}</Badge>
			<span>
				{v.canonicalSchema.name}@{v.canonicalSchema.version}
			</span>
			{canManage && (
				<Button
					variant="secondary"
					size="sm"
					aria-label={`Edit ${definitionKey} version ${v.version}`}
					onPress={() => onEdit(definitionKey, v.version)}
				>
					Edit
				</Button>
			)}
			{canManage && v.status === ImportDefinitionStatus.DRAFT && (
				<Button
					variant="primary"
					size="sm"
					aria-label={`Activate ${definitionKey} version ${v.version}`}
					onPress={() => onActivate(definitionKey, v.version)}
				>
					Activate
				</Button>
			)}
		</li>
	)
}

/** The tenant's import definitions and their versions (TO-400 SCN-010). Pure. */
export function ImportDefinitionList({ definitions, canManage, onNew, onEdit, onActivate }: ImportDefinitionListProps) {
	return (
		<section aria-label="Import definitions" className="flex flex-col gap-3 text-ds-sm text-ds-text">
			{canManage && (
				<div>
					<Button variant="primary" size="sm" onPress={onNew}>
						New definition
					</Button>
				</div>
			)}
			{definitions.length === 0 && <p>No import definitions yet.</p>}
			<ul className="flex flex-col gap-3">
				{definitions.map((definition) => (
					<li key={definition.key} className="rounded-ds-control border border-ds-border p-ds-3">
						<h4 className="font-ds-heading">{definition.key}</h4>
						<ul className="flex flex-col gap-1">
							{definition.versions.map((v) => (
								<VersionRow
									key={v.version}
									definitionKey={definition.key}
									version={v}
									canManage={canManage}
									onEdit={onEdit}
									onActivate={onActivate}
								/>
							))}
						</ul>
					</li>
				))}
			</ul>
		</section>
	)
}
